import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import TabbedDetailCard from '#/modules/commonComponents/layout/TabbedDetailCard';
import { useCan } from '#/modules/infra/auth/useCan';
import PurchaseAmendmentCreateAction from '#/modules/srmMgmt/purchaseOrder/components/PurchaseAmendmentCreateAction';
import PurchaseOrderAmendmentsSection from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderAmendmentsSection';
import PurchaseOrderArrivalScheduleSection from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderArrivalScheduleSection';
import PurchaseOrderInboundHistorySection from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderInboundHistorySection';
import PurchaseOrderLinesSection from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderLinesSection';
import PurchaseOrderPaymentPlanSection from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderPaymentPlanSection';
import { PURCHASE_AMENDMENT_KIND } from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentKinds';
import {
  canAmendPurchaseOrder,
  canCreateArrivalSchedule,
  canCreateStockReceiptFromPurchaseOrder,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';

function renderArrivalScheduleCreateAction(purchaseOrderId, enabled) {
  if (enabled && purchaseOrderId) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to={ROUTES.purchaseOrderArrivalScheduleCreate(purchaseOrderId)}>
          到貨排程
        </Link>
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" variant="outline" disabled>
      到貨排程
    </Button>
  );
}

function renderInboundCreateAction(purchaseOrderId, enabled) {
  if (enabled && purchaseOrderId) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to={ROUTES.purchaseOrderInboundCreate(purchaseOrderId)}>新增入庫</Link>
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" variant="outline" disabled>
      新增入庫
    </Button>
  );
}

const PurchaseOrderDetailTabs = ({ detail, onRefetch }) => {
  const canCreateSchedule = canCreateArrivalSchedule(detail?.status);
  const canCreateInbound = canCreateStockReceiptFromPurchaseOrder(detail?.status);
  const canAmend = canAmendPurchaseOrder(detail?.status);
  const canUpdate = useCan(PERM.PURCHASE_ORDER_UPDATE);

  return (
    <TabbedDetailCard defaultValue="lines">
      <TabbedDetailCard.List>
        <TabbedDetailCard.Trigger value="lines">採購明細</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="arrival">到貨排程</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="inbound">入庫歷史</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="paymentPlan">付款計畫</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="amendments">異動歷史</TabbedDetailCard.Trigger>
      </TabbedDetailCard.List>

      <TabbedDetailCard.Pane value="lines" title="採購明細">
        <PurchaseOrderLinesSection lines={detail?.lines ?? []} />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="arrival"
        title="到貨排程"
        actions={renderArrivalScheduleCreateAction(detail?.id, canCreateSchedule)}
      >
        <PurchaseOrderArrivalScheduleSection purchaseOrderId={detail?.id} />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="inbound"
        title="入庫歷史"
        actions={renderInboundCreateAction(detail?.id, canCreateInbound)}
      >
        <PurchaseOrderInboundHistorySection purchaseOrderId={detail?.id} />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane value="paymentPlan" title="付款計畫">
        <PurchaseOrderPaymentPlanSection
          kind={PURCHASE_AMENDMENT_KIND.PURCHASE_ORDER}
          orderId={detail?.id}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="amendments"
        title="異動歷史"
        actions={(
          <PurchaseAmendmentCreateAction
            orderId={detail?.id}
            enabled={canUpdate && canAmend}
            amendments={detail?.amendments}
            createPath={ROUTES.purchaseOrderAmendmentCreate}
            editPath={ROUTES.purchaseOrderAmendmentEdit}
          />
        )}
      >
        <PurchaseOrderAmendmentsSection
          kind={PURCHASE_AMENDMENT_KIND.PURCHASE_ORDER}
          detail={detail}
          onRefetch={onRefetch}
        />
      </TabbedDetailCard.Pane>
    </TabbedDetailCard>
  );
};

PurchaseOrderDetailTabs.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
    amendments: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default PurchaseOrderDetailTabs;
